import React, {Children, isValidElement} from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import Chart from './Chart'
import Bar from './Bar'
import {stack, stackOffsetNone} from 'd3-shape'
import {scaleLinear, scaleQuantize, scaleBand} from 'd3-scale'
import Title from 'components/Title'
import range from 'lodash/range'

const getMax = arr => Math.max.apply(null, arr)
const getMin = arr => Math.min.apply(null, arr)

export default class StackedBarChart extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    data: PropTypes.array,
    xAxisKey: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    title: PropTypes.node,
    stacked: PropTypes.bool,
    innerPadding: PropTypes.number,
    outerPadding: PropTypes.number,
    barWidth: PropTypes.number,
    quantize: PropTypes.number,
    tooltip: PropTypes.func
  }

  static defaultProps = {
    margin: {top: 30, right: 20, bottom: 40, left: 60},
    innerPadding: 2,
    outerPadding: 8,
    stacked: false
  }

  render() {
    const {height, className} = this.props

    return (
      <div className={classnames('stacked-bar-chart', className)}>
        {this.renderTitle()}
        <Chart height={height}>
          {this.renderBars()}
        </Chart>
      </div>
    )
  }

  renderTitle() {
    const {title} = this.props

    if (title) {
      if (isValidElement(title)) {
        return title
      } else {
        return <Title>{title}</Title>
      }
    } else {
      return null
    }
  }

  renderBars() {
    const {stacked} = this.props

    if (stacked) {
      return this.renderStackedBars()
    } else {
      return this.renderSimpleBars()
    }
  }

  renderSimpleBars() {
    const {
      data,
      width,
      height,
      min,
      max,
      quantize,
      barWidth,
      xAxisKey,
      children,
      innerPadding,
      outerPadding,
      tooltip
    } = this.props
    const bars = Children.toArray(children)

    const scaleBars = scaleBand()
      .domain(data.map(item => item[xAxisKey]))
      .range([0, width])
      .paddingInner(innerPadding)
      .paddingOuter(outerPadding)

    const yScale = scaleQuantize()
      .domain([min, max])
      .range(range(1, quantize + 1))

    return bars.map((bar, index) => {
      return data.map(item => {
        const value = item[bar.props.dataKey] === true ? max : item[bar.props.dataKey]
        if (!!value === false) return null
        const quantHeight = height / quantize

        return (
          <Bar
            color={bar.props.color}
            x={scaleBars(item[xAxisKey])}
            y={(quantize - yScale(value)) * quantHeight}
            width={barWidth || scaleBars.bandwidth()}
            height={yScale(value) * quantHeight}
            opacity={bar.props.opacity}
            tooltip={tooltip ? tooltip(item[xAxisKey], value) : null}
            key={item[xAxisKey]}
          />
        )
      })
    })
  }

  renderStackedBars() {
    const {
      data,
      children,
      height,
      width,
      xAxisKey,
      stacked,
      min,
      max,
      outerPadding
    } = this.props
    const bars = Children.toArray(children)
    const keys = bars.map(item => item.props.dataKey)
    const series = stack().offset(stackOffsetNone).keys(keys)(data)
    const maxValue = max || getMax(series.map(item => getMax(item.map(item => Math.max(item[0], item[1])))))
    const minValue = min || getMin(series.map(item => getMin(item.map(item => Math.min(item[0], item[1])))))

    const scaleBars = scaleBand()
      .domain(data.map(item => item[xAxisKey]))
      .rangeRound([0, width])
      .paddingOuter(outerPadding)
      .paddingInner(0.3)

    const yScale = scaleLinear()
      .domain([minValue, maxValue])
      .range([height, 0])

    return series.map((serie, barIndex) => {
      return serie.map((item, index) => {
        const barHeight = yScale(stacked ? item[0] : 0) - yScale(item[1])

        return (
          <Bar
            color={bars[barIndex].props.color}
            x={scaleBars(data[index][xAxisKey]) + (barIndex * 3)}
            y={yScale(item[1])}
            width={scaleBars.bandwidth()}
            height={barHeight}
          />
        )
      })
    })
  }

  renderLabel(x, y, text) {
    return <text x={x} y={y} dy='.5em' className='stacked-bar-chart_stack-label'>{text}</text>
  }
}



// WEBPACK FOOTER //
// ./client/components/Chart/StackedBarChart.js